/**
 * Cross-platform location service.
 *
 * On web / PWA it uses the standard `navigator.geolocation` API.
 * On native (iOS/Android via Capacitor) it uses `@capacitor/geolocation`
 * for foreground tracking and `@capacitor-community/background-geolocation`
 * for true background tracking (keeps emitting updates when the app is
 * backgrounded via an Android foreground service + persistent notification).
 *
 * The public API is intentionally thin so components can swap in without
 * changing their existing call-sites:
 *
 *   const id = await locationService.watchPosition(onPos, onErr, opts);
 *   await locationService.clearWatch(id);
 *   const pos = await locationService.getCurrentPosition(opts);
 *
 * Background-specific API (no-ops on web):
 *   const id = await locationService.startBackgroundTracking(onPos, opts);
 *   await locationService.stopBackgroundTracking(id);
 */

import { Capacitor } from '@capacitor/core';

const isNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

const toStdPosition = (pos) => ({
  coords: {
    latitude: pos.coords?.latitude ?? pos.latitude,
    longitude: pos.coords?.longitude ?? pos.longitude,
    altitude: pos.coords?.altitude ?? pos.altitude ?? null,
    accuracy: pos.coords?.accuracy ?? pos.accuracy ?? null,
    altitudeAccuracy: pos.coords?.altitudeAccuracy ?? null,
    heading: pos.coords?.heading ?? pos.heading ?? null,
    speed: pos.coords?.speed ?? pos.speed ?? null,
  },
  timestamp: pos.timestamp ?? pos.time ?? Date.now(),
});

class LocationService {
  constructor() {
    this._browserWatchIds = new Map(); // ourId -> browserWatchId
    this._nativeWatchIds = new Map();  // ourId -> capacitorWatchId
    this._bgWatchIds = new Map();      // ourId -> capgo watcher id
    this._wakeLock = null;             // Screen Wake Lock sentinel (PWA)
    this._wakeLockRefCount = 0;
    this._visibilityHandlerBound = false;
    this._nextId = 1;
  }

  isNative() { return isNative(); }

  // --- Screen Wake Lock (PWA) -------------------------------------------
  // Keeps the screen on while tracking is active so the OS doesn't throttle
  // geolocation updates. Auto re-requests when the page regains focus
  // (wake locks are released when a tab goes background).
  async _acquireWakeLock() {
    if (isNative()) return; // Native plugins manage their own foreground service
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
    this._wakeLockRefCount++;
    if (this._wakeLock) return;
    try {
      this._wakeLock = await navigator.wakeLock.request('screen');
      this._wakeLock.addEventListener('release', () => {
        this._wakeLock = null;
      });
    } catch (e) {
      console.warn('[LocationService] wakeLock.request failed', e);
    }
    if (!this._visibilityHandlerBound) {
      document.addEventListener('visibilitychange', this._onVisibilityChange);
      this._visibilityHandlerBound = true;
    }
  }

  _onVisibilityChange = async () => {
    if (document.visibilityState === 'visible' && this._wakeLockRefCount > 0 && !this._wakeLock) {
      try {
        this._wakeLock = await navigator.wakeLock.request('screen');
      } catch { /* ignore */ }
    }
  };

  async _releaseWakeLock() {
    if (this._wakeLockRefCount > 0) this._wakeLockRefCount--;
    if (this._wakeLockRefCount === 0) {
      try { if (this._wakeLock) await this._wakeLock.release(); } catch { /* ignore */ }
      this._wakeLock = null;
      if (this._visibilityHandlerBound) {
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
        this._visibilityHandlerBound = false;
      }
    }
  }

  async requestPermissions() {
    if (!isNative()) return true; // browser prompts on first use
    try {
      const { Geolocation } = await import('@capacitor/geolocation');
      const res = await Geolocation.requestPermissions();
      return res.location === 'granted' || res.coarseLocation === 'granted';
    } catch (e) {
      console.warn('[LocationService] requestPermissions failed', e);
      return false;
    }
  }

  async getCurrentPosition(options = {}) {
    const { enableHighAccuracy = true, timeout = 10000, maximumAge = 0 } = options;
    if (!isNative()) {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (p) => resolve(toStdPosition(p)),
          (e) => reject(e),
          { enableHighAccuracy, timeout, maximumAge }
        );
      });
    }
    const { Geolocation } = await import('@capacitor/geolocation');
    const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy, timeout, maximumAge });
    return toStdPosition(pos);
  }

  /**
   * Foreground watch. Returns a numeric ID compatible with clearWatch.
   * Signature mirrors `navigator.geolocation.watchPosition(success, error, opts)`.
   */
  async watchPosition(onPosition, onError, options = {}) {
    const ourId = this._nextId++;
    const { enableHighAccuracy = true, timeout = 10000, maximumAge = 0 } = options;

    if (!isNative()) {
      const browserId = navigator.geolocation.watchPosition(
        (p) => onPosition && onPosition(toStdPosition(p)),
        (e) => onError && onError(e),
        { enableHighAccuracy, timeout, maximumAge }
      );
      this._browserWatchIds.set(ourId, browserId);
      return ourId;
    }

    const { Geolocation } = await import('@capacitor/geolocation');
    const capId = await Geolocation.watchPosition(
      { enableHighAccuracy, timeout, maximumAge },
      (pos, err) => {
        if (err) { onError && onError(err); return; }
        if (pos) onPosition && onPosition(toStdPosition(pos));
      }
    );
    this._nativeWatchIds.set(ourId, capId);
    return ourId;
  }

  async clearWatch(ourId) {
    if (ourId == null) return;
    if (this._browserWatchIds.has(ourId)) {
      navigator.geolocation.clearWatch(this._browserWatchIds.get(ourId));
      this._browserWatchIds.delete(ourId);
      return;
    }
    if (this._nativeWatchIds.has(ourId)) {
      try {
        const { Geolocation } = await import('@capacitor/geolocation');
        await Geolocation.clearWatch({ id: this._nativeWatchIds.get(ourId) });
      } catch (e) { /* ignore */ }
      this._nativeWatchIds.delete(ourId);
    }
  }

  /**
   * Background-capable tracking (native only). On web it falls back to
   * a regular watchPosition so callers get something instead of nothing.
   * Returns an ID that must be passed to stopBackgroundTracking().
   */
  async startBackgroundTracking(onPosition, options = {}) {
    if (!isNative()) {
      // PWA: acquire a screen wake-lock so the OS keeps emitting GPS as long
      // as the screen is on (best-effort; still dies when the app is fully
      // backgrounded — only real native builds can survive that).
      await this._acquireWakeLock();
      return this.watchPosition(onPosition, null, options);
    }
    const ourId = this._nextId++;
    const {
      enableHighAccuracy = true,
      minimumUpdateInterval = 2000,
      backgroundTitle = 'RollM8s',
      backgroundMessage = 'Sharing your live ride location',
    } = options;
    try {
      const { BackgroundGeolocation } = await import('@capacitor-community/background-geolocation');
      const watcherId = await BackgroundGeolocation.addWatcher(
        {
          backgroundTitle,
          backgroundMessage,
          requestPermissions: true,
          stale: false,
          distanceFilter: 5,
          minimumUpdateInterval,
          enableHighAccuracy,
        },
        (location, err) => {
          if (err) {
            console.warn('[LocationService] bg err', err);
            return;
          }
          if (location) onPosition && onPosition(toStdPosition(location));
        }
      );
      this._bgWatchIds.set(ourId, watcherId);
      return ourId;
    } catch (e) {
      console.warn('[LocationService] bg plugin unavailable, falling back to foreground watch', e);
      return this.watchPosition(onPosition, null, options);
    }
  }

  async stopBackgroundTracking(ourId) {
    if (ourId == null) return;
    if (this._bgWatchIds.has(ourId)) {
      try {
        const { BackgroundGeolocation } = await import('@capacitor-community/background-geolocation');
        await BackgroundGeolocation.removeWatcher({ id: this._bgWatchIds.get(ourId) });
      } catch (e) { /* ignore */ }
      this._bgWatchIds.delete(ourId);
      return;
    }
    // Fell back to foreground watch — release the wake lock
    await this.clearWatch(ourId);
    await this._releaseWakeLock();
  }
}

export const locationService = new LocationService();
export default locationService;
